import React, { Fragment, type ReactNode } from 'react';
import Divider from '../../atoms/Divider';
import {
  TOOLBAR_DENSITY_REGULAR,
  TOOLBAR_ORIENTATION_HORIZONTAL,
  TOOLBAR_TONE_SURFACE,
  TOOLBAR_VARIANT_FLAT,
  type ToolbarDensity,
  type ToolbarOrientation,
  type ToolbarTone,
  type ToolbarVariant,
} from './Toolbar.constants';
import { Group as GroupRoot, Root } from './Toolbar.styled';

export interface ToolbarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The toolbar contents — typically a sequence of `<Toolbar.Group>`
   * children. A single `Divider` (atom) is auto-inserted between every two
   * adjacent groups so consumers don't have to manage separators by hand.
   * Non-group children (e.g. ad-hoc spacers, custom slots) pass through
   * unchanged and don't get a divider injected around them.
   */
  children: ReactNode;
  /** Layout direction. Defaults to `horizontal`. */
  orientation?: ToolbarOrientation;
  /**
   * Visual treatment. `flat` (default) is transparent — for toolbars that
   * sit inside a container. `floating` adds a rounded surface with
   * elevation — for toolbars that lift above a canvas.
   */
  variant?: ToolbarVariant;
  /** Surface of the `floating` variant. Defaults to `surface`. */
  tone?: ToolbarTone;
  /** Inter-button gap density. Defaults to `regular`. */
  density?: ToolbarDensity;
  /**
   * Margin (px) on each side of the auto-injected `<Divider>` between
   * adjacent groups. Defaults to `0` so `density` alone controls the
   * inter-group spacing — bump this when the toolbar wants more
   * breathing room around the dividers without widening the gap
   * inside groups (e.g. the floating canvas toolbar's 8px sep margin
   * vs the 2px button-to-button gap).
   */
  dividerInset?: number | string;
  /**
   * Required for screen readers — toolbars need an accessible name.
   * Defaults to `"Toolbar"`; pass something specific (e.g. `"Editor
   * controls"`) for context.
   */
  ariaLabel?: string;
  dataTestId?: string;
}

export interface ToolbarGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode;
  ariaLabel?: string;
  dataTestId?: string;
}

interface GroupComponent extends React.FC<ToolbarGroupProps> {
  /** Discriminator the parent `<Toolbar>` looks for to inject dividers. */
  __isToolbarGroup: true;
}

/** Internal context used solely to thread orientation + density into Group. */
const ToolbarContext = React.createContext<{
  orientation: ToolbarOrientation;
  density: ToolbarDensity;
}>({
  orientation: TOOLBAR_ORIENTATION_HORIZONTAL,
  density: TOOLBAR_DENSITY_REGULAR,
});

const ToolbarGroup: GroupComponent = (({
  children,
  ariaLabel,
  dataTestId = 'toolbar-group',
  ...rest
}) => {
  const { orientation, density } = React.useContext(ToolbarContext);
  return (
    <GroupRoot
      // Spread `rest` FIRST so the explicit props below (role, aria,
      // data-testid, internal $orientation/$density) always win. Letting
      // a caller pass `role="banner"` or `data-testid="..."` and have
      // it override Toolbar's internal semantics would silently break
      // the Divider weaving + a11y wiring downstream.
      {...rest}
      $orientation={orientation}
      $density={density}
      role="group"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {children}
    </GroupRoot>
  );
}) as GroupComponent;
ToolbarGroup.__isToolbarGroup = true;

const isToolbarGroup = (node: React.ReactNode): node is React.ReactElement =>
  React.isValidElement(node) &&
  (node.type as { __isToolbarGroup?: boolean })?.__isToolbarGroup === true;

const ToolbarRoot: React.FC<ToolbarProps> = ({
  children,
  orientation = TOOLBAR_ORIENTATION_HORIZONTAL,
  variant = TOOLBAR_VARIANT_FLAT,
  tone = TOOLBAR_TONE_SURFACE,
  density = TOOLBAR_DENSITY_REGULAR,
  dividerInset = 0,
  ariaLabel = 'Toolbar',
  dataTestId = 'toolbar',
  ...rest
}) => {
  // Walk children and inject a `<Divider />` between every two adjacent
  // `<Toolbar.Group>` siblings. Non-group children (custom slots, raw
  // buttons) pass through verbatim — they don't trigger a separator.
  const items = React.Children.toArray(children);
  const dividerOrientation =
    orientation === TOOLBAR_ORIENTATION_HORIZONTAL ? 'vertical' : 'horizontal';
  const woven: ReactNode[] = [];
  let lastWasGroup = false;
  items.forEach((child, idx) => {
    const isGroup = isToolbarGroup(child);
    if (isGroup && lastWasGroup) {
      woven.push(
        <Divider
          key={`__divider-${idx}`}
          orientation={dividerOrientation}
          inset={dividerInset}
          dataTestId={`${dataTestId}__divider`}
        />,
      );
    }
    woven.push(<Fragment key={`__item-${idx}`}>{child}</Fragment>);
    lastWasGroup = isGroup;
  });

  return (
    <ToolbarContext.Provider value={{ orientation, density }}>
      <Root
        // Spread `rest` FIRST so the explicit props below (role,
        // aria-orientation/label, data-testid + data-* tags) always
        // win over caller-supplied DOM attrs. See ToolbarGroup above.
        {...rest}
        $orientation={orientation}
        $variant={variant}
        $tone={tone}
        $density={density}
        role="toolbar"
        aria-orientation={orientation}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-orientation={orientation}
        data-variant={variant}
        data-tone={tone}
        data-density={density}
      >
        {woven}
      </Root>
    </ToolbarContext.Provider>
  );
};

/**
 * Grouped horizontal (or vertical) bar of related controls. Compose with
 * `<Toolbar.Group>` children — a `Divider` is auto-injected between every
 * two adjacent groups so consumers never have to think about separators.
 *
 * @example
 *   <Toolbar variant="floating" ariaLabel="Editor controls">
 *     <Toolbar.Group ariaLabel="View">
 *       <IconButton ariaLabel="Zoom in" icon={<MdZoomIn />} />
 *       <IconButton ariaLabel="Zoom out" icon={<MdZoomOut />} />
 *     </Toolbar.Group>
 *     <Toolbar.Group ariaLabel="Edit">
 *       <IconButton ariaLabel="Undo" icon={<MdUndo />} />
 *       <IconButton ariaLabel="Redo" icon={<MdRedo />} />
 *     </Toolbar.Group>
 *   </Toolbar>
 */
const Toolbar = ToolbarRoot as typeof ToolbarRoot & {
  Group: typeof ToolbarGroup;
};
Toolbar.Group = ToolbarGroup;

export default Toolbar;
